/* API Telesales (/api/v2/telesale): đổi thẻ, nhu cầu, 5 bước, cọc. Khách mẫu (isDemoId) không gọi các hàm này, vẫn lưu trên máy */
import dayjs from 'dayjs';
import { authFetch } from '../../../services/mixin';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import { depositFromServer, mergeServerDeposits, mergeServerQuotes, type DepositApiAction, type ServerDeposit, type ServerQuote } from './deposit';
import { mergeTouches, shortTag, type Needs, type ServerTouch, type ServerWorkStep } from './logic';
import type { DepositCustomer, Quote } from './quote';
import { useLeadStore } from './store';
import './touchSync';

const BASE = '/api/v2/telesale';

export interface ApiResult<T> { ok: boolean; data: T | null; message: string; code?: string; /** Thân phản hồi lỗi (VD id phiếu khi 409) */ body?: any; status?: number }

export const apiRequest = async <T,>(url: string, init: RequestInit = {}): Promise<ApiResult<T>> => {
  try {
    const res = await authFetch(url, {
      ...init,
      headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
    }, { keepForbidden: true });
    const json = await res.json().catch(() => null);
    // API v2 trả success, API cũ (call-logs) trả status kiểu đúng sai
    const ok = res.ok && json?.success !== false && json?.status !== false;
    const detail = json?.errors ? Object.values(json.errors).flat()[0] as string | undefined : undefined;
    return {
      ok,
      data: ok ? (json?.data as T) : null,
      message: detail || json?.message || json?.msg || (ok ? '' : `Lỗi máy chủ (HTTP ${res.status})`),
      code: json?.code,
      status: res.status,
      ...(ok ? {} : { body: json }),
    };
  } catch (e: any) {
    return { ok: false, data: null, message: e?.message === 'Unauthorized' ? 'Phiên đăng nhập đã hết hạn' : 'Không kết nối được máy chủ' };
  }
};

const send = <T,>(url: string, method: 'POST' | 'PATCH', body?: unknown) =>
  apiRequest<T>(url, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

/* ---------- Báo danh sách cập nhật dòng khách khi hồ sơ đổi dữ liệu ---------- */

export type CustomerPatch = Partial<Pick<ChannelCustomerItem, 'customer_tag' | 'customer_tag_id' | 'dynamic_next_tags' | 'mang' | 'needs' | 'has_received_deposit'>>;
type Listener = (id: number, patch: CustomerPatch) => void;
const listeners = new Set<Listener>();
export const onCustomerPatched = (fn: Listener) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};
export const emitCustomerPatched: Listener = (id, patch) => listeners.forEach((fn) => fn(id, patch));

/* ---------- Thẻ S ---------- */

type TagData = Required<Pick<CustomerPatch, 'customer_tag' | 'customer_tag_id' | 'dynamic_next_tags'>>;

/** Đổi Trạng thái CS trên server (ma trận có thể chặn); xong thì bỏ thẻ trên máy vì tagOf ưu tiên thẻ trên máy */
export const saveCustomerTag = async (id: number, tagId: number) => {
  const res = await send<TagData>(`${BASE}/customers/${id}/tag`, 'PATCH', { customer_tag_id: tagId });
  if (res.ok && res.data) {
    useLeadStore.getState().patch(id, { tagId: undefined });
    emitCustomerPatched(id, res.data);
  }
  return res;
};

/* ---------- Nhu cầu ---------- */

/** needs gửi cả phiếu vì server ghi đè; kênh khách muốn chưa có cột riêng nên nằm trong needs.kenh */
export const saveCustomerNeeds = async (id: number, body: { mang?: string; needs: Needs & { kenh?: string } }) => {
  const res = await send<Pick<ChannelCustomerItem, 'mang' | 'needs'>>(`${BASE}/customers/${id}/needs`, 'PATCH', body);
  if (res.ok && res.data) emitCustomerPatched(id, res.data);
  return res;
};

/* ---------- 5 bước ---------- */

export const getWorkSteps = (id: number) =>
  apiRequest<{ current_step: string | null; all_done: boolean; steps: ServerWorkStep[] }>(`${BASE}/customers/${id}/work-steps`);

/* ---------- Nhật ký cuộc gọi ---------- */

export const postCallLog = (b: { phone: string; direction: 'in' | 'out'; answered: boolean; duration: number; note?: string }) =>
  send<{ id: number }>('/api/v1/be/call-logs', 'POST', {
    client_request_id: `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`,
    phone: b.phone,
    direction: b.direction,
    outcome: b.answered ? 'answered' : 'no_answer',
    duration: b.duration,
    started_at: dayjs().subtract(b.duration, 'second').format('YYYY-MM-DD HH:mm:ss'),
    manual: true,
    ...(b.note?.trim() ? { note: b.note.trim() } : {}),
  });

/* ---------- Lịch sử chạm ---------- */

/** Tải lịch sử chạm về máy: gộp để đổi máy vẫn thấy, và để hiện các dòng server tự ghi (báo giá) */
export const reloadTouches = async (id: number) => {
  const res = await apiRequest<{ items: ServerTouch[]; total: number }>(`${BASE}/customers/${id}/touches?per_page=100`);
  if (!res.ok || !res.data) return;
  const st = useLeadStore.getState();
  const cur = st.byId[id]?.touches ?? [];
  const next = mergeTouches(cur, res.data.items);
  if (JSON.stringify(next) !== JSON.stringify(cur)) st.patch(id, { touches: next });
};

/* ---------- Báo giá ---------- */

export interface QuoteBody {
  action: 'draft' | 'submit';
  dich_vu: string;
  items: { id: number | null; name: string; price: number | null; card: unknown }[];
  promo: string | null;
  discount: number;
  reason: string | null;
  channel: string;
  send_at: string | null;
  valid_until: string;
  replaces_id: number | null;
  deposit: { amount: number; purpose: string; due_at: string } | null;
}
export const createQuote = (customerId: number, b: QuoteBody) => send<ServerQuote>(`${BASE}/customers/${customerId}/quotes`, 'POST', b);
export const updateQuote = (quoteId: number, b: QuoteBody) => apiRequest<ServerQuote>(`${BASE}/quotes/${quoteId}`, { method: 'PUT', body: JSON.stringify(b) });
export const quoteAct = (quoteId: number, act: 'send' | 'approve' | 'reject' | 'replied' | 'cancel', reason?: string) =>
  send<ServerQuote>(`${BASE}/quotes/${quoteId}/${act}`, 'POST', reason ? { reason } : undefined);

/** Tải báo giá và cọc về máy. orphans: khoản cọc cũ không gắn báo giá nào trên máy này, chỉ hiện ở bảng Cọc */
export const reloadQuotes = async (id: number, cust: DepositCustomer) => {
  const [q, d] = await Promise.all([
    apiRequest<{ items: ServerQuote[]; can_approve: boolean }>(`${BASE}/customers/${id}/quotes`),
    getDeposits(id),
  ]);
  const st = useLeadStore.getState();
  const cur = st.byId[id]?.quotes ?? [];
  const quotes = q.ok && q.data ? mergeServerQuotes(cur, q.data.items, cust) : cur;
  const merged = d.ok && d.data ? mergeServerDeposits(quotes, d.data.items, cust) : { quotes, orphans: [] as Quote[] };
  if (JSON.stringify(merged.quotes) !== JSON.stringify(cur)) st.patch(id, { quotes: merged.quotes });
  return {
    orphans: merged.orphans,
    canApprove: !!q.data?.can_approve,
    canConfirm: !!d.data?.can_confirm,
    error: !q.ok ? q.message : !d.ok ? d.message : undefined,
  };
};

/* ---------- Cọc ---------- */

export const getDeposits = (customerId: number) =>
  apiRequest<{ items: ServerDeposit[]; can_confirm: boolean }>(`${BASE}/customers/${customerId}/deposits`);

/** Cùng khách, cùng quote_code thì server trả khoản đã có, nên gửi lại an toàn */
export const createDeposit = async (customerId: number, b: { quote_code: string; amount: number; purpose: string; due_at: string; note: string }) => {
  const res = await send<ServerDeposit | { deposit: ServerDeposit }>(`${BASE}/customers/${customerId}/deposits`, 'POST', b);
  const data = res.data && 'deposit' in res.data ? res.data.deposit : res.data as ServerDeposit | null;
  return { ...res, data };
};

interface DepositActData {
  deposit: ServerDeposit;
  tag_changed?: boolean;
  customer_tag?: CustomerPatch['customer_tag'];
  dynamic_next_tags?: CustomerPatch['dynamic_next_tags'];
  to_tag_id?: number;
}

/**
 * Báo biên lai, chưa khớp, xác nhận nhận cọc trên server rồi cập nhật bản trên máy.
 * Trả về lỗi nếu không làm được. Khoản chỉ có trên server (máy khác tạo báo giá) thì không có bản trên máy để sửa.
 */
export async function runDepositApi(leadId: number, serverId: number, act: DepositApiAction, by: string): Promise<string | undefined> {
  const url = `${BASE}/deposits/${serverId}/${act.type}`;
  const body = act.type === 'reject' ? { note: act.note } : act.type === 'confirm' && act.bankRef ? { bank_ref: act.bankRef } : undefined;
  const res = await send<DepositActData>(url, 'POST', body);
  if (!res.ok || !res.data) return res.message || 'Không cập nhật được khoản cọc';
  const { deposit } = res.data;
  const st = useLeadStore.getState();
  const quotes = st.byId[leadId]?.quotes;
  if (quotes?.some((q) => q.deposit?.serverId === serverId)) {
    st.patch(leadId, { quotes: quotes.map((q) => (q.deposit?.serverId === serverId ? { ...q, deposit: depositFromServer(deposit, q.deposit) } : q)) });
  }
  const text = {
    receipt: `Khách gửi biên lai cọc ${deposit.code} → chờ Kế toán đối soát`,
    reject: `Kế toán: cọc ${deposit.code} chưa khớp sao kê — ${act.type === 'reject' ? act.note : ''}`,
    confirm: `Kế toán xác nhận đã nhận cọc ${deposit.code}`,
  }[act.type];
  st.addTouch(leadId, { kind: 'note', text, by });
  if (act.type === 'confirm') {
    if (res.data.tag_changed) {
      st.patch(leadId, { tagId: undefined });
      st.addTouch(leadId, { kind: 'stage', text: `Mã S → ${shortTag(res.data.customer_tag?.name)} · tự chuyển khi xác nhận cọc`, by });
    }
    emitCustomerPatched(leadId, {
      has_received_deposit: true,
      ...(res.data.tag_changed ? { customer_tag: res.data.customer_tag, customer_tag_id: res.data.to_tag_id, dynamic_next_tags: res.data.dynamic_next_tags } : {}),
    });
  }
  return undefined;
}
